'use client';

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    type JSX,
    type PropsWithChildren,
} from 'react';

import { AiContentType } from '@videoblocks/events-ts/lib/storyblocks/content/ai/AiContentTypeEnum';

import { fetchCredits } from '../_actions/fetchCredits';
import { type RecentGenerationResult } from '../_actions/fetchRecentGeneration';
import { VIDEO_GENERATION_FORM_DEFAULTS } from './VideoGenerationFormState';
import { useImageSearch } from './imageSearch/useImageSearch';
import { useImageGenerationPolling } from './polling/useImageGenerationPolling';
import { useVideoGenerationPolling } from './polling/useVideoGenerationPolling';
import { CONFIRMATION_MODAL_DEFAULTS, useConfirmationModal } from './reducers/useConfirmationModal';
import { IMAGE_GENERATION_SUBMIT_DEFAULTS, useImageGenerationSubmit } from './reducers/useImageGenerationSubmit';
import { VIDEO_GENERATION_SUBMIT_DEFAULTS, useVideoGenerationSubmit } from './reducers/useVideoGenerationSubmit';
import {
    VOICEOVER_GENERATION_SUBMIT_DEFAULTS,
    useVoiceoverGenerationSubmit,
} from './reducers/useVoiceoverGenerationSubmit';
import {
    AI_TOOLKIT_CATEGORIES,
    AI_USER_SEGMENTS,
    type ActiveTask,
    type AiToolkitActiveStatus,
    type AiToolkitSubmitStatus,
    type AiUserSegment,
    type AiToolkitCategory,
    type AiToolkitContextValue,
    type AiToolkitFormStates,
    type AiToolkitSubmitStates,
    type ImageGenerationSubmitState,
    type ImageSearchReturn,
    type VideoGenerationSubmitState,
} from './types';

type AcknowledgedTaskStatuses = Partial<Record<AiToolkitCategory, AiToolkitActiveStatus>>;

const noop = (): void => {};
const asyncNoop = async (): Promise<void> => {};

const DEFAULT_IMAGE_SEARCH: ImageSearchReturn = {
    searchTerm: '',
    setSearchTerm: noop,
    mediaType: 'Images',
    setMediaType: noop,
    results: [],
    isLoading: true,
    error: null,
    hasSearched: false,
    totalCount: 0,
    hasMore: false,
    loadMore: asyncNoop,
    search: asyncNoop,
    reset: noop,
    currentPage: 1,
    totalPages: 1,
    goToPage: asyncNoop,
    showSearch: false,
    setShowSearch: noop,
};

export const DEFAULT_FORM_STATES: AiToolkitFormStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: VIDEO_GENERATION_FORM_DEFAULTS,
};

export const DEFAULT_SUBMIT_STATES: AiToolkitSubmitStates = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: VIDEO_GENERATION_SUBMIT_DEFAULTS,
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: null,
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: IMAGE_GENERATION_SUBMIT_DEFAULTS,
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: VOICEOVER_GENERATION_SUBMIT_DEFAULTS,
};

export const DEFAULT_CATEGORY_SEGMENTS: Record<AiToolkitCategory, AiUserSegment> = {
    [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: AI_USER_SEGMENTS.UNSUBSCRIBED,
    [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: AI_USER_SEGMENTS.UNSUBSCRIBED,
    [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: AI_USER_SEGMENTS.UNSUBSCRIBED,
    [AI_TOOLKIT_CATEGORIES.VOICEOVER]: AI_USER_SEGMENTS.UNSUBSCRIBED,
};

export const AiToolkitContext = createContext<AiToolkitContextValue>({
    credits: 0,
    isEnterprise: false,
    subscriptionId: null,
    categorySegments: DEFAULT_CATEGORY_SEGMENTS,
    activeTask: null,
    acknowledgeTask: noop,
    refreshCredits: () => Promise.resolve(0),
    formStates: DEFAULT_FORM_STATES,
    submitStates: DEFAULT_SUBMIT_STATES,
    updateFormState: noop,
    dispatchVideoGenerationSubmit: noop,
    dispatchImageGenerationSubmit: noop,
    dispatchVoiceoverGenerationSubmit: noop,
    confirmationModal: CONFIRMATION_MODAL_DEFAULTS,
    dispatchConfirmationModal: noop,
    imageSearch: DEFAULT_IMAGE_SEARCH,
    videoGenerationFileErrorMessage: null,
    setVideoGenerationFileErrorMessage: noop,
    imageGenerationFileErrorMessage: null,
    setImageGenerationFileErrorMessage: noop,
    videoGenerationSubmitError: null,
    setVideoGenerationSubmitError: noop,
    imageGenerationSubmitError: null,
    setImageGenerationSubmitError: noop,
});

export type AiToolkitProviderProps = PropsWithChildren<{
    initialCredits: number;
    isEnterprise: boolean;
    subscriptionId: number | null;
    categorySegments: Record<AiToolkitCategory, AiUserSegment>;
    initialVideoGenerationResult?: RecentGenerationResult | null;
    initialVideoAiContentIds?: { aiContentId: string; lastAiContentId: string };
    initialImageGenerationResult?: RecentGenerationResult | null;
    initialImageAiContentIds?: { aiContentId: string; lastAiContentId: string };
}>;

function mapTaskTypeToAiContentType(taskType: string | null): AiContentType | null {
    switch (taskType) {
        case 'image_to_video':
            return AiContentType.IMAGE_TO_VIDEO;
        case 'text_to_video':
            return AiContentType.TEXT_TO_VIDEO;
        case 'text_to_image':
            return AiContentType.TEXT_TO_IMAGE;
        case 'image_to_image':
            return AiContentType.IMAGE_TO_IMAGE;
        default:
            return null;
    }
}

function computeInitialVideoState(
    result: RecentGenerationResult | null | undefined,
    seedIds: { aiContentId: string; lastAiContentId: string } | undefined,
): VideoGenerationSubmitState {
    const base: VideoGenerationSubmitState = {
        ...VIDEO_GENERATION_SUBMIT_DEFAULTS,
        aiContentId: seedIds?.aiContentId ?? '',
        lastAiContentId: seedIds?.lastAiContentId ?? '',
    };
    if (!result) return base;
    if (result.state === 'inProgress') {
        return { ...base, status: 'inProgress', pendingTaskId: result.taskId };
    }
    return {
        ...base,
        status: 'success',
        videoUrl: result.downloadUrl,
        aiContentType: mapTaskTypeToAiContentType(result.taskType),
        restoredFromHistory: true,
    };
}

function computeInitialImageState(
    result: RecentGenerationResult | null | undefined,
    seedIds: { aiContentId: string; lastAiContentId: string } | undefined,
): ImageGenerationSubmitState {
    const base: ImageGenerationSubmitState = {
        ...IMAGE_GENERATION_SUBMIT_DEFAULTS,
        aiContentId: seedIds?.aiContentId ?? '',
        lastAiContentId: seedIds?.lastAiContentId ?? '',
    };
    if (!result) return base;
    if (result.state === 'inProgress') {
        return { ...base, status: 'inProgress', pendingTaskId: result.taskId };
    }
    return {
        ...base,
        status: 'success',
        imageUrl: result.downloadUrl,
        aiContentType: mapTaskTypeToAiContentType(result.taskType),
        restoredFromHistory: true,
    };
}

function deriveActiveTask(
    videoState: AiToolkitSubmitStates[typeof AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION],
    imageState: AiToolkitSubmitStates[typeof AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION],
    voiceoverStatus: AiToolkitSubmitStatus,
    acknowledgedTaskStatuses: AcknowledgedTaskStatuses,
): ActiveTask {
    const entries: Array<{ category: AiToolkitCategory; status: AiToolkitSubmitStatus; exclude?: boolean }> = [
        {
            category: AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION,
            status: videoState.status,
            exclude:
                videoState.restoredFromHistory ||
                acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION] === videoState.status,
        },
        {
            category: AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION,
            status: imageState.status,
            exclude:
                imageState.restoredFromHistory ||
                acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION] === imageState.status,
        },
        {
            category: AI_TOOLKIT_CATEGORIES.VOICEOVER,
            status: voiceoverStatus,
            exclude: acknowledgedTaskStatuses[AI_TOOLKIT_CATEGORIES.VOICEOVER] === voiceoverStatus,
        },
    ];
    const inProgress = entries.find((e) => e.status === 'inProgress' && !e.exclude);
    if (inProgress) return { category: inProgress.category, status: 'inProgress' };
    const success = entries.find((e) => e.status === 'success' && !e.exclude);
    if (success) return { category: success.category, status: 'success' };
    const error = entries.find((e) => e.status === 'error' && !e.exclude);
    if (error) return { category: error.category, status: 'error' };
    return null;
}

export const AiToolkitProvider = ({
    children,
    initialCredits,
    isEnterprise,
    subscriptionId,
    categorySegments,
    initialVideoGenerationResult,
    initialVideoAiContentIds,
    initialImageGenerationResult,
    initialImageAiContentIds,
}: AiToolkitProviderProps): JSX.Element => {
    const [credits, setCredits] = useState(initialCredits);
    const [formStates, setFormStates] = useState<AiToolkitFormStates>({});
    const [videoGenerationFileErrorMessage, setVideoGenerationFileErrorMessage] = useState<string | null>(null);
    const [imageGenerationFileErrorMessage, setImageGenerationFileErrorMessage] = useState<string | null>(null);
    const [videoGenerationSubmitError, setVideoGenerationSubmitError] = useState<string | null>(null);
    const [imageGenerationSubmitError, setImageGenerationSubmitError] = useState<string | null>(null);
    const [videoGenerationSubmitState, dispatchVideoGenerationSubmit] = useVideoGenerationSubmit(
        computeInitialVideoState(initialVideoGenerationResult, initialVideoAiContentIds),
    );
    const [imageGenerationSubmitState, dispatchImageGenerationSubmit] = useImageGenerationSubmit(
        computeInitialImageState(initialImageGenerationResult, initialImageAiContentIds),
    );
    const [voiceoverGenerationSubmitState, dispatchVoiceoverGenerationSubmit] = useVoiceoverGenerationSubmit();
    const [confirmationModal, dispatchConfirmationModal] = useConfirmationModal();
    const imageSearch = useImageSearch();
    const [acknowledgedTaskStatuses, setAcknowledgedTaskStatuses] = useState<AcknowledgedTaskStatuses>({});

    const acknowledgeTask = useCallback((category: AiToolkitCategory, status: AiToolkitActiveStatus): void => {
        setAcknowledgedTaskStatuses((prev) => ({ ...prev, [category]: status }));
    }, []);

    // Once a category starts a new attempt, any acknowledgment of its previous outcome no longer
    // applies to the outcome this attempt will produce.
    useEffect(() => {
        setAcknowledgedTaskStatuses((prev) => {
            const next = { ...prev };
            let changed = false;
            for (const [category, status] of [
                [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION, videoGenerationSubmitState.status],
                [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION, imageGenerationSubmitState.status],
                [AI_TOOLKIT_CATEGORIES.VOICEOVER, voiceoverGenerationSubmitState.status],
            ] as const) {
                if (status === 'inProgress' && category in next) {
                    delete next[category];
                    changed = true;
                }
            }
            return changed ? next : prev;
        });
    }, [videoGenerationSubmitState.status, imageGenerationSubmitState.status, voiceoverGenerationSubmitState.status]);

    const activeTask = useMemo(
        () =>
            deriveActiveTask(
                videoGenerationSubmitState,
                imageGenerationSubmitState,
                voiceoverGenerationSubmitState.status,
                acknowledgedTaskStatuses,
            ),
        [
            videoGenerationSubmitState,
            imageGenerationSubmitState,
            voiceoverGenerationSubmitState.status,
            acknowledgedTaskStatuses,
        ],
    );

    const submitStates: AiToolkitSubmitStates = useMemo(
        () => ({
            [AI_TOOLKIT_CATEGORIES.VIDEO_GENERATION]: videoGenerationSubmitState,
            [AI_TOOLKIT_CATEGORIES.VIDEO_EDIT]: null,
            [AI_TOOLKIT_CATEGORIES.IMAGE_GENERATION]: imageGenerationSubmitState,
            [AI_TOOLKIT_CATEGORIES.VOICEOVER]: voiceoverGenerationSubmitState,
        }),
        [videoGenerationSubmitState, imageGenerationSubmitState, voiceoverGenerationSubmitState],
    );

    const refreshCredits = useCallback(async (): Promise<number> => {
        const balance = await fetchCredits();
        setCredits(balance);
        return balance;
    }, []);

    const updateFormState = useCallback(
        <K extends keyof AiToolkitFormStates>(category: K, state: AiToolkitFormStates[K]): void => {
            setFormStates((prev) => ({ ...prev, [category]: state }));
        },
        [],
    );

    useVideoGenerationPolling({ videoGenerationSubmitState, dispatchVideoGenerationSubmit, refreshCredits });
    useImageGenerationPolling({ imageGenerationSubmitState, dispatchImageGenerationSubmit, refreshCredits });

    return (
        <AiToolkitContext.Provider
            value={{
                credits,
                isEnterprise,
                subscriptionId,
                categorySegments,
                activeTask,
                acknowledgeTask,
                refreshCredits,
                formStates,
                submitStates,
                updateFormState,
                dispatchVideoGenerationSubmit,
                dispatchImageGenerationSubmit,
                dispatchVoiceoverGenerationSubmit,
                confirmationModal,
                dispatchConfirmationModal,
                imageSearch,
                videoGenerationFileErrorMessage,
                setVideoGenerationFileErrorMessage,
                imageGenerationFileErrorMessage,
                setImageGenerationFileErrorMessage,
                videoGenerationSubmitError,
                setVideoGenerationSubmitError,
                imageGenerationSubmitError,
                setImageGenerationSubmitError,
            }}
        >
            {children}
        </AiToolkitContext.Provider>
    );
};

export const useAiToolkitContext = (): AiToolkitContextValue => {
    const context = useContext(AiToolkitContext);

    return context;
};
